import React, { useEffect, useRef, useState } from 'react'
import { MissioncastRow, MissionCastProps } from '../../interfaces/missioncast'
import { MissionCastExportPreview } from './MissionCastExportPreview'
import { toPng } from 'html-to-image'
import jsPDF from 'jspdf'
import { exportTableToExcel } from '../../utils/exportTableToExcel'

const metricMapping = {
  timeStamp: 'Date (MM/DD)',
  Weather: 'Weather',
  'Chc Of Precip (%)': 'Precip (%)',

  // Ceiling
  'Ceiling Height (FT)': 'Ceiling (FT)',
  'Ceiling Height (M)':  'Ceiling (M)',

  // Visibility
  'SFC Visibility (SM)':    'Visibility (SM)',
  'SFC Visibility (Miles)': 'Visibility (Miles)',
  'SFC Visibility (KM)':    'Visibility (KM)',

  // SFC Wind
  'SFC Wind (°/KT)':  'Wind (°/KT)',
  'SFC Wind (°/MPH)': 'Wind (°/MPH)',
  'SFC Wind (°/MPS)': 'Wind (°/MPS)',

  // 250FT Wind
  '250FT Wind (°/KT)':  '250FT Wind (°/KT)',
  '250FT Wind (°/MPH)': '250FT Wind (°/MPH)',
  '250FT Wind (°/MPS)': '250FT Wind (°/MPS)',

  // Wind Gust
  'Wind Gust (KTS)': 'Wind Gust (KTS)',
  'Wind Gust (MPH)': 'Wind Gust (MPH)',
  'Wind Gust (MPS)': 'Wind Gust (MPS)',

  // SFC Temp
  'SFC Temp (F)': 'Temp (F)',
  'SFC Temp (C)': 'Temp (C)',

  // Dewpoint
  'SFC Dp Temp (F)': 'Dp Temp (F)',
  'SFC Dp Temp (C)': 'Dp Temp (C)',

  // Pressure
  'Pressure (hPa)':  'Pressure (hPa)',
  'Pressure (inHg)': 'Pressure (inHg)',
  'Pressure (mb)':   'Pressure (mb)',

  // Total Precipitation
  'Total Precip (MM/HR)': 'Precip (MM/HR)',
  'Total Precip (IN/HR)': 'Precip (IN/HR)',

  // Ground Temp
  'Ground Temp (°F)': 'Ground Temp (°F)',
  'Ground Temp (°C)': 'Ground Temp (°C)',
}

const colorMapping = {
  Red: 'bg-missioncastRed text-thresholdRedText',
  Yellow: 'bg-missioncastYellow text-thresholdYellowText',
  Green: 'bg-missioncastGreen text-thresholdGreenText',
  White: 'bg-[#FCFCFC]',
}

const roadcastkeysMapping = {
  timeStamp: 'Date (MM/DD)',
  'Ground Temp (°F)': 'Ground Temp (°F)',
  'Ground Temp (°C)': 'Ground Temp (°C)',
  'Surface Grip (0-1)': 'Surface Grip',
  'Road Condition': 'Road Condition',
  Slickness: 'Slickness',
  'Ice Probability (%)': 'Ice Probability (%)',
}

function formatToShortDate(dateString: string): string {
  try {
    // Extract datetime and timezone parts
    const [datetimePart, timezone] = dateString.split(' America/')
    const parsedDate = new Date(datetimePart)

    if (isNaN(parsedDate.getTime())) {
      throw new Error('Invalid date format')
    }

    const date = parsedDate.getDate() // 1-31
    const month = parsedDate.getMonth() + 1 // 0-based, so +1

    return `${month}/${date}`
  } catch (err) {
    console.error('Failed to parse date:', err)
    return dateString // fallback to original
  }
}

const convertTo24Format = (dateString: string) => {
  // Extract the time portion (like "02:00:00 PM")
  const match = dateString.match(/(\d{1,2}:\d{2})(?::\d{2})?\s?(AM|PM)?/)
  if (!match) return null

  let [_, time, meridian] = match

  // Convert to 24-hour format if AM/PM present
  let [hours, minutes] = time.split(':').map(Number)

  if (meridian) {
    if (meridian === 'PM' && hours !== 12) hours += 12
    if (meridian === 'AM' && hours === 12) hours = 0
  }

  // Return formatted as HH:mm (always 2 digits)
  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}`
}

const parseForecastTransposed = (
  forecast: Map<string, MissioncastRow>,
  isRoadData: boolean = false,
  isVP:boolean = false
): { parsedRows: MissioncastRow[] } => {
  const parsedRows = []
  if(!isVP){
    const keyMapping = isRoadData ? roadcastkeysMapping : metricMapping
    for (const entry of forecast) {
      const parsedEntry = {}
      for (const key of Object.keys(entry)) {
        const mappedKey = keyMapping[key]
        if (key === 'timeStamp') {
          parsedEntry[mappedKey] = {
            value: formatToShortDate(entry[key]),
            color: 'White',
          }
        } else {
          parsedEntry[mappedKey] = {
            value: entry[key].Data ?? 'N/A',
            color: entry[key].Color ?? 'White',
          }
        }
      }
      parsedEntry['time'] = convertTo24Format(entry['timeStamp'])
      parsedRows.push(parsedEntry)
    }
  }
  else{
    for (const entry of forecast) {
      const parsedEntry = {}
      for (const key of Object.keys(entry)) {
        if (key === 'timeStamp') {
          parsedEntry[key] = {
            value: formatToShortDate(entry[key]),
            color: 'White',
          }
        } else {
          parsedEntry[key] = {
            value: entry[key].Data ?? 'N/A',
            color: entry[key].Color ?? 'White',
          }
        }
      }
      parsedEntry['time'] = convertTo24Format(entry['timeStamp'])
      parsedRows.push(parsedEntry)
    }
  }
  
  return { parsedRows }
}

const MissionCastTable: React.FC<MissionCastProps> = ({
  data,
  coordinates,
}) => {
  const [shortRange, setShortRange] = useState<MissioncastRow[]>([])
  const [extendedRange, setExtendedRange] = useState<MissioncastRow[]>([])
  const [activeTab, setActiveTab] = useState<'short' | 'extended' | 'road' | 'vp'>(
    'short'
  )
  const [roadRange, setRoadRange] = useState([])
  const [vpRange, setVPRange] = useState([])

  const firstTimestamp = data.Hourly
    ? data.Hourly[Object.keys(data.Hourly)[0]].timeStamp
    : null

  const cleanedTimestamp = firstTimestamp?.replace(/ [A-Za-z/_]+$/, '') // remove " America/New_York"
  const readableDate = cleanedTimestamp
    ? new Date(cleanedTimestamp).toISOString().slice(0, 10) // returns 'YYYY-MM-DD'
    : null

  useEffect(() => {
    const hourly = []
    for (const slot of Object.values(data['Hourly'])) {
      hourly.push(slot)
    }
    const extended = []
    for (const slot of Object.values(data['Three Hourly'])) {
      extended.push(slot)
    }
    for (const slot of Object.values(data['Six Hourly'])) {
      extended.push(slot)
    }
    const { parsedRows: shortRows } = parseForecastTransposed(hourly, false)
    const { parsedRows: extendedRows } = parseForecastTransposed(
      extended,
      false
    )
    setShortRange(shortRows)
    setExtendedRange(extendedRows)

    const road = []
    if (data['Road']) {
      for (const slot of Object.values(data['Road'])) {
        road.push(slot)
      }
    }

    const { parsedRows: roadRows } = parseForecastTransposed(road, true)
    setRoadRange(roadRows)

    const verticalProfile = []
    if(data["VP"]){
      for (const slot of Object.values(data['VP'])) {
        verticalProfile.push(slot)
      }
    }
    const { parsedRows: vpRows } = parseForecastTransposed(verticalProfile, false, true)
    setVPRange(vpRows)
    
  }, [data])

  const renderTransposedTable = (
    rows: MissioncastRow[],
    isExtendedForecast = false,
    isRoadData = false,
    isVPData = false,
    clip = false
  ) => {
    const allKeys = [
      'Date (MM/DD)',
      'Weather',
      'Precip (%)',
      'Ceiling (FT)',
      'Ceiling (M)',
      'Visibility (SM)',
      'Visibility (Miles)',
      'Visibility (KM)',
      'Wind (°/KT)',
      'Wind (°/MPH)',
      'Wind (°/MPS)',
      '250FT Wind (°/KT)',
      '250FT Wind (°/MPH)',
      '250FT Wind (°/MPS)',
      'Wind Gust (KTS)',
      'Wind Gust (MPH)',
      'Wind Gust (MPS)',
      'Temp (F)',
      'Temp (C)',
      'Dp Temp (F)',
      'Dp Temp (C)',
      'Pressure (hPa)',
      'Pressure (inHg)',
      'Pressure (mb)',
      'Precip (MM/HR)',
      'Precip (IN/HR)',
      'Ground Temp (°F)',
      'Ground Temp (°C)',
      'Surface Grip',
      'Road Condition',
      'Slickness',
      'Ice Probability (%)',
    ]

    const extendedKeys = [
      'Date (MM/DD)',
      'Weather',
      'Visibility (SM)',
      'Wind (°/KT)',
      '250FT Wind (°/KT)',
      'Wind Gust (KTS)',
      'Temp (F)',
      'Dp Temp (F)',
      'Pressure (hPa)',
    ]

    // Pick the correct set of keys
    // const keysToRender = isRoadData
    //   ? Object.values(roadcastkeysMapping)
    //   : isExtendedForecast
    //   ? extendedKeys
    //   : allKeys
    
    var commonKeys = allKeys.filter(key => rows.some(obj => key in obj));

    if(isVPData && rows.length > 0){
      function getHeight(key) {
        if (key === 'timeStamp') return Infinity
        if (key.startsWith('SFC')) return 0
        const match = key.match(/^(\d+)FT/)
        return match ? parseInt(match[1], 10) : Infinity
      }
      commonKeys = Object.keys(rows[0]).filter(key => key != 'time')
      commonKeys = [...commonKeys].sort((a, b) => getHeight(a) - getHeight(b)).reverse()
    }
    
    return (
      <>
      {
        isVPData ? 
        <div className={`${clip ? 'overflow-clip p-2' : 'overflow-auto'}`}>
          <table className="table-auto w-full border border-black text-md font-semibold">
            <thead>
              <tr>
                <th className="border border-gray-800 bg-[#FCFCFC] text-left">
                  Local Time
                </th>
                {rows.map((row, idx) => (
                  <th
                    key={idx}
                    className="border border-gray-800 text-center bg-[#FCFCFC] text-nowrap whitespace-wrap"
                  >
                    {row.time}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {commonKeys.map((key, i) => (
                <tr key={i}>
                  <td className="border border-gray-800 bg-[#FCFCFC] whitespace-nowrap font-semibold">
                    {key == 'timeStamp' ? "Date (MM/DD)" : key}
                  </td>
                  {rows.map((row, j) => {
                    const cell = row[key]
                    return (
                      <td
                        key={j}
                        className={`border border-gray-800 text-center ${
                          cell?.color ? colorMapping[cell.color] : ''
                        } whitespace-wrap`}
                      >
                        {cell ?  cell.value : 'N/A'}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div> : 
        <div className={`${clip ? 'overflow-clip p-2' : 'overflow-auto'}`}>
          <table className="table-auto w-full border border-black text-md font-semibold">
            <thead>
              <tr>
                <th className="border border-gray-800 bg-[#FCFCFC] text-left">
                  Local Time
                </th>
                {rows.map((row, idx) => (
                  <th
                    key={idx}
                    className="border border-gray-800 text-center bg-[#FCFCFC] text-nowrap whitespace-wrap"
                  >
                    {row.time}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {commonKeys.map((key, i) => (
                <tr key={i}>
                  <td className="border border-gray-800 bg-[#FCFCFC] whitespace-nowrap font-semibold">
                    {key}
                  </td>
                  {rows.map((row, j) => {
                    const cell = row[key]
                    return (
                      <td
                        key={j}
                        className={`border border-gray-800 text-center ${
                          cell?.color ? colorMapping[cell.color] : ''
                        } whitespace-wrap`}
                      >
                        {cell
                          ? cell.value === 'No Ceiling'
                            ? 'NONE'
                            : key === 'Slickness' && cell.value === 'NORMAL'
                            ? 'NRL'
                            : cell.value
                          : 'N/A'}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
      </>
    )
  }

  const [exportOption, setExportOption] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(event.target as Node)
      ) {
        setExportOption(false);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const exportButtonClass = "bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded inline-flex items-center cursor-pointer flex flex-row justify-between gap-2"
  const ExportIcon = ({ className = "w-4 h-4" }) => (
    <svg
      className={className}
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 16 18"
    >
      <path
        stroke="currentColor"
        strokeWidth="2"
        d="M8 1v11m0 0 4-4m-4 4L4 8m11 4v3a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-3"
      />
    </svg>
  );
  const Spinner = () => (
    <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
  );

  const exportRef = useRef<HTMLDivElement | null>(null);
  function appendFormattedDateToString(baseString: string) {
    const date = new Date()
  
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
  
    const formattedDate = `-${year}${month}${day}-${hours}${minutes}`
  
    return baseString + formattedDate
  }
  const filter = (node: HTMLElement) => {
    const exclusionClasses = ["action-buttons"];
      return !exclusionClasses.some((cls) =>
        node.classList?.contains(cls)
      );
  };
 
  const [exporting, setExporting] = useState<null | "png" | "pdf" | "excel">(null);
  
  const handleExportPNG = async () => {
    const element = exportRef.current;
    if (!element || exporting) return;
    setExporting("png");
    
    try{
      const dataUrl = await toPng(element, {
        filter,
        backgroundColor: "#fff",
      });

      const link = document.createElement("a");
      link.download = appendFormattedDateToString("MissionCAST") + ".png";
      link.href = dataUrl;
      link.click();
    }
    finally{
      setExporting(null);
      setExportOption(false)
    }
  };
  
  const handleExportPDF = async () => {
    const element = exportRef.current;
    if (!element || exporting) return;
    setExporting("pdf");
   
    try {
      const imgData = await toPng(element, {
        filter,
        backgroundColor: "#fff",
      });

      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "a4",
      });

      const imgProps = pdf.getImageProperties(imgData);

      const margin = 20;
      const pdfWidth = pdf.internal.pageSize.getWidth() - margin * 2;
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      const pageHeight = pdf.internal.pageSize.getHeight() - margin * 2;

      let finalWidth = pdfWidth;
      let finalHeight = pdfHeight;

      if (pdfHeight > pageHeight) {
        const ratio = pageHeight / pdfHeight;
        finalWidth = pdfWidth * ratio;
        finalHeight = pageHeight;
      }

      const x = margin + (pdfWidth - finalWidth) / 2;

      pdf.addImage(imgData, "PNG", x, margin, finalWidth, finalHeight);

      pdf.save(appendFormattedDateToString("MissionCAST") + ".pdf");
    } catch (err) {
      console.error("PDF export failed", err);
    }
    finally{
      setExporting(null);
      setExportOption(false)
    }
  };
  
  const handleExportExcel = (e?: React.MouseEvent) => {
    const element = exportRef.current;
    if (!element || exporting) return;
    e?.stopPropagation();
    setExporting("excel");
    try{
      exportTableToExcel(
        element,
        appendFormattedDateToString("MissionCAST") + ".xlsx"
      );
    }
    finally {
      setExporting(null);
      setExportOption(false)
    }
  };

  const exportOptions = [
    { type: "png", label: "PNG", handler: handleExportPNG },
    { type: "pdf", label: "PDF", handler: handleExportPDF },
    { type: "excel", label: "Excel", handler: handleExportExcel },
  ];
  
  return (
    <div className='w-auto'>
      <div className="flex flex-row justify-between">
          <span className="text-base text-left font-semibold date">
            Date: {readableDate}
          </span>
          <span className="text-base text-left font-semibold coordinates">
            Coordinates: {' (' + coordinates[0] + ', ' + coordinates[1] + ') '}
          </span>
      </div>
      <div className="flex flex-row justify-between content-center gap-2">
       

        <div className="mb-4 space-x-2 text-center self-center">
          <button
            onClick={() => setActiveTab('short')}
            className={`px-4 py-2 rounded font-bold text-md ${
              activeTab === 'short'
                ? 'bg-twsBlue text-white'
                : 'bg-gray-300 text-black'
            }`}
          >
            Short Range (0–24 hrs)
          </button>
          <button
            onClick={() => setActiveTab('extended')}
            className={`px-4 py-2 rounded font-bold text-md ${
              activeTab === 'extended'
                ? 'bg-twsBlue text-white'
                : 'bg-gray-300 text-black'
            }`}
          >
            Extended Range (24–144 hrs)
          </button>
          {roadRange.length > 0 && (
            <button
              onClick={() => setActiveTab('road')}
              className={`px-4 py-2 rounded font-bold text-md ${
                activeTab === 'road'
                  ? 'bg-twsBlue text-white'
                  : 'bg-gray-300 text-black'
              }`}
            >
              Road Conditions (0-24 hrs)
            </button>
          )}
          {vpRange.length > 0 && (
            <button
              onClick={() => setActiveTab('vp')}
              className={`px-4 py-2 rounded font-bold text-md ${
                activeTab === 'vp'
                  ? 'bg-twsBlue text-white'
                  : 'bg-gray-300 text-black'
              }`}
            >
              Vertical Wind Profile (NowCast)
            </button>
          )}
        </div>
        
        <div ref={exportMenuRef}>
          <div className='bg-gray-300 h-8 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded inline-flex items-center cursor-pointer'
            onClick={() => setExportOption(!exportOption)}>
            Export
          </div>
          {
            exportOption &&
            <div className='flex flex-col gap-1 rounded-md border p-2 w-fit divide-x bg-white absolute top-9 right-0'>
              {exportOptions.map(({ type, label, handler }) => (
              <div
                key={type}
                className={`${exportButtonClass} ${exporting ? "pointer-events-none opacity-70" : ""}`}
                onClick={handler}
              >
                <span>
                  {exporting === type ? "Exporting..." : label}
                </span>

                {exporting === type ? <Spinner /> : <ExportIcon />}
              </div>
            ))}
            </div>
          }
        </div>
      </div>

      {activeTab === 'short'
        ? renderTransposedTable(shortRange)
        : activeTab === 'extended'
        ? renderTransposedTable(extendedRange, true)
        : activeTab === 'vp'
        ? renderTransposedTable(vpRange, false, false, true)
        : renderTransposedTable(roadRange, false, true)}
      <div className="text-left font-bold flex flex-col">
        <div>NA = Data is not available. Condition is unknown. </div>
        <div>NONE = The condition (Ceiling or Wind Gusts) is not present. </div>
        <div>NRL = The condition (Slickness) is NORMAL. </div>
      </div>

      <div
        style={{
          position: "absolute",
          left: "-9999px",
          top: 0,
        }}
      >
        <MissionCastExportPreview
          exportRef={exportRef}
          shortRange={shortRange}
          extendedRange={extendedRange}
          renderTransposedTable={renderTransposedTable}
          readableDate={readableDate}
          coordinates={coordinates}
          roadRange={roadRange}
          vpData={vpRange}
        />
      </div>
    </div>
  )
}

export default MissionCastTable
